import { Entity, CrudOptions } from '../../types/builder';

interface Props {
  entity: Entity;
  onUpdate: (entity: Entity) => void;
}

const METHOD_STYLES: Record<string, string> = {
  GET:    'bg-green-500/10 text-green-400 border-green-500/20',
  POST:   'bg-blue-500/10 text-blue-400 border-blue-500/20',
  PUT:    'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  DELETE: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const CrudTab = ({ entity, onUpdate }: Props) => {
  const plural = entity.name.toLowerCase() + 's';
  const base = `/api/${plural}`;

  const crudItems: {
    key: keyof CrudOptions;
    method: string;
    path: string;
    desc: string;
  }[] = [
    { key: 'getAll',  method: 'GET',    path: base,           desc: `Get all ${entity.name}s` },
    { key: 'getById', method: 'GET',    path: `${base}/{id}`, desc: `Get by ID` },
    { key: 'create',  method: 'POST',   path: base,           desc: `Create ${entity.name}` },
    { key: 'update',  method: 'PUT',    path: `${base}/{id}`, desc: `Update ${entity.name}` },
    { key: 'delete',  method: 'DELETE', path: `${base}/{id}`, desc: `Delete ${entity.name}` },
  ];

  const toggleCrud = (
    key: keyof CrudOptions,
    value: boolean
  ) => {
    onUpdate({
      ...entity,
      crud: { ...entity.crud, [key]: value }
    });
  };

  const enabledCount = Object.values(entity.crud)
    .filter(Boolean).length;

  return (
    <div>

      {/* Info */}
      <div className="flex items-center
                      justify-between mb-3">
        <span className="text-gray-500 text-xs">
          Toggle individual CRUD endpoints:
        </span>
        <span className="text-blue-400
                         font-mono text-xs">
          {enabledCount}/5 enabled
        </span>
      </div>

      {/* CRUD List */}
      <div className="bg-gray-800/50
                      border border-gray-700
                      rounded-xl overflow-hidden">
        {crudItems.map((item, idx) => (
          <div
            key={item.key}
            className={`flex items-center
                        gap-3 px-4 py-3
                        cursor-pointer
                        hover:bg-gray-800
                        transition-colors
                        ${idx < crudItems.length - 1
                          ? 'border-b border-gray-700'
                          : ''
                        }
                        ${!entity.crud[item.key]
                          ? 'opacity-50'
                          : ''
                        }`}
            onClick={() => toggleCrud(
              item.key,
              !entity.crud[item.key]
            )}>

            <input
              type="checkbox"
              checked={entity.crud[item.key]}
              onChange={e => toggleCrud(
                item.key, e.target.checked
              )}
              onClick={e => e.stopPropagation()}
              className="accent-blue-500
                         w-4 h-4 cursor-pointer"
            />

            <span className={`font-mono text-xs
                              font-bold px-2 py-1
                              rounded border
                              min-w-[52px]
                              text-center
                              ${METHOD_STYLES[item.method]}`}>
              {item.method}
            </span>

            <span className="font-mono text-xs
                             text-gray-300 flex-1">
              {item.path}
            </span>

            <span className="text-gray-600 text-xs">
              {item.desc}
            </span>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onUpdate({
            ...entity,
            crud: {
              getAll: true, getById: true,
              create: true, update: true,
              delete: true
            }
          })}
          className="flex-1 text-xs
                     text-gray-500
                     hover:text-blue-400
                     border border-gray-700
                     hover:border-blue-500
                     rounded-lg py-1.5
                     transition-colors">
          Enable All
        </button>
        <button
          onClick={() => onUpdate({
            ...entity,
            crud: {
              getAll: true, getById: true,
              create: false, update: false,
              delete: false
            }
          })}
          className="flex-1 text-xs
                     text-gray-500
                     hover:text-yellow-400
                     border border-gray-700
                     hover:border-yellow-500
                     rounded-lg py-1.5
                     transition-colors">
          Read Only
        </button>
        <button
          onClick={() => onUpdate({
            ...entity,
            crud: {
              getAll: false, getById: false,
              create: false, update: false,
              delete: false
            }
          })}
          className="flex-1 text-xs
                     text-gray-500
                     hover:text-red-400
                     border border-gray-700
                     hover:border-red-500
                     rounded-lg py-1.5
                     transition-colors">
          Disable All
        </button>
      </div>
    </div>
  );
};

export default CrudTab;